import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  TypographyElements,
  BasicCard,
  BasicCardVariants,
} from '@carboledger/ui';
import { PageContainer } from '@/components/layout';
import { useCurrentUser, useTranslations } from '@/hooks';
import { MODULES, ROUTES } from '@/app/routes';
import styles from './HomePage.module.scss';

// Home launcher hub — welcome heading over grouped sections of action tiles.
export const HomePage = () => {
  const navigate = useNavigate();
  const t = useTranslations();
  const user = useCurrentUser();
  const displayName = user?.name ?? '';

  const sections = useMemo(
    () => [
      {
        key: 'modules',
        title: t('HOME_MODULES'),
        cards: [
          {
            key: 'iscc-eu',
            label: t('HOME_MODULE_ISCC_EU'),
            variant: BasicCardVariants.filled,
            onClick: () => navigate(ROUTES.contracts(MODULES.isccEu)),
          },
          {
            key: 'iscc-plus',
            label: t('HOME_MODULE_ISCC_PLUS'),
            variant: BasicCardVariants.muted,
            // onClick: () => navigate(ROUTES.contracts(MODULES.isccPlus)),
          },
          {
            key: 'iscc-corsia',
            label: t('HOME_MODULE_ISCC_CORSIA'),
            variant: BasicCardVariants.muted,
          },
        ],
      },
      {
        key: 'networks',
        title: t('HOME_NETWORKS'),
        cards: [
          {
            key: 'suppliers',
            label: t('HOME_MANAGE_SUPPLIERS'),
            variant: BasicCardVariants.default,
            onClick: () => navigate(ROUTES.suppliers),
          },
          {
            key: 'customers',
            label: t('HOME_MANAGE_CUSTOMERS'),
            variant: BasicCardVariants.default,
            onClick: () => navigate(ROUTES.customers),
          },
        ],
      },
      {
        key: 'workspace-setup',
        title: t('HOME_WORKSPACE_SETUP'),
        cards: [
          {
            key: 'account-setup',
            label: t('ACCOUNT_SETUP_TITLE'),
            variant: BasicCardVariants.default,
            onClick: () => navigate(ROUTES.accountSetup()),
          },
          {
            key: 'admin-center',
            label: t('HOME_ADMIN_CENTER'),
            variant: BasicCardVariants.default,
            onClick: () => navigate(ROUTES.admin),
          },
        ],
      },
    ],
    [t, navigate],
  );

  return (
    <PageContainer className={styles.page}>
      <Typography
        variant={TypographyVariants.h5}
        as={TypographyElements.h1}
        color={TypographyColors.primary}
      >
        {t('HOME_WELCOME', { name: displayName })}
      </Typography>

      {sections.map((section) => (
        <section key={section.key} className={styles.section}>
          <Typography
            variant={TypographyVariants.body}
            weight={TypographyWeights.semibold}
            color={TypographyColors.primary}
            as={TypographyElements.h2}
          >
            {section.title}
          </Typography>
          <div className={styles.cards}>
            {section.cards.map((card) => (
              <BasicCard
                key={card.key}
                variant={card.variant}
                onClick={card.onClick}
                className={styles.card}
              >
                {card.label}
              </BasicCard>
            ))}
          </div>
        </section>
      ))}
    </PageContainer>
  );
};
